import { useEffect, useState } from "react";
import { X, Users, Loader2, Forward } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

export function ForwardDialog({ message, onClose, dark = false, onForwarded }) {
  const [convs, setConvs] = useState(null);
  const [sendingTo, setSendingTo] = useState(null);

  useEffect(() => {
    if (message) {
      setConvs(null);
      api.get("/chat/conversations").then(({ data }) => setConvs(data)).catch(() => setConvs([]));
    }
  }, [message]);

  if (!message) return null;

  const preview =
    message.text ||
    (message.attachment
      ? message.attachment.kind === "audio"
        ? "🎤 Sprachnachricht"
        : `📎 ${message.attachment.filename || "Datei"}`
      : message.content_ref
        ? `📌 ${message.content_ref.title || "Beitrag"}`
        : "");

  const forward = async (c) => {
    if (sendingTo) return;
    setSendingTo(c.conv);
    try {
      const { data } = await api.post("/chat/messages", {
        conv: c.conv,
        text: message.text || "",
        attachment: message.attachment || null,
        content_ref: message.content_ref || null,
        forwarded_from: { user_name: message.user_name || "" },
      });
      toast.success(`Weitergeleitet an ${c.type === "team" ? "Team-Kanal" : c.name} ➤`);
      onForwarded?.(c.conv, data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Weiterleiten fehlgeschlagen");
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[10013] flex items-end sm:items-center justify-center" data-testid="chat-forward-dialog">
      <div className="absolute inset-0 bg-black/40" data-testid="chat-forward-backdrop" onClick={onClose} />
      <div
        className={`relative w-full sm:w-[380px] mx-0 sm:mx-3 rounded-t-2xl sm:rounded-2xl shadow-2xl border overflow-hidden ${
          dark ? "bg-neutral-800 border-neutral-700" : "bg-white border-neutral-200"
        }`}
      >
        <div className={`flex items-center justify-between px-4 py-3 border-b ${dark ? "border-neutral-700" : "border-neutral-100"}`}>
          <div className="min-w-0">
            <div className={`flex items-center gap-1.5 text-sm font-bold ${dark ? "text-white" : "text-neutral-900"}`}>
              <Forward className="h-4 w-4" /> Weiterleiten an…
            </div>
            <p className={`text-[11px] truncate mt-0.5 ${dark ? "text-neutral-400" : "text-neutral-400"}`}>{preview.slice(0, 70)}</p>
          </div>
          <button data-testid="chat-forward-close" onClick={onClose} className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${dark ? "text-neutral-300 hover:bg-neutral-700" : "text-neutral-500 hover:bg-neutral-100"}`}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-1.5">
          {convs === null && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          )}
          {convs?.map((c) => (
            <button
              key={c.conv}
              data-testid={`chat-forward-target-${c.conv}`}
              onClick={() => forward(c)}
              disabled={!!sendingTo}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition disabled:opacity-50 ${
                dark ? "hover:bg-neutral-700 text-neutral-100" : "hover:bg-neutral-50 text-neutral-800"
              }`}
            >
              {c.type === "team" ? (
                <span className="h-9 w-9 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <Users className="h-4 w-4" />
                </span>
              ) : (
                <UserAvatar src={c.user?.avatar_url} name={c.name} color={c.user?.avatar_color} className="h-9 w-9" textClass="text-[11px]" />
              )}
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold truncate">{c.type === "team" ? "Team-Kanal" : c.name}</span>
                <span className={`block text-[11px] ${dark ? "text-neutral-400" : "text-neutral-400"}`}>
                  {c.type === "team" ? "Alle Teammitglieder" : c.user?.role === "admin" ? "Redaktion" : "Creator"}
                </span>
              </span>
              {sendingTo === c.conv && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
